/**
 * FilmRedoDialog — the standard DTF film-only redo confirm.
 *
 * One wrapper for the identical RedoReasonDialog wiring that grew on four
 * stations (film-print, film-storage inspect, matching, pressing): the fixed
 * film-failure reason set + the standard per-unit copy, all keyed off the
 * shared `filmPrint.redo*` strings. A new film redo reason is added HERE once.
 *
 * The film-print station's BULK dialog keeps its own copy block — it reuses
 * only `useFilmRedoReasons`.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RedoReasonDialog, type RedoReasonOption } from './RedoReasonDialog';

/** Fixed DTF-film redo reasons (the PET film is the consumable — these are
 *  film-failure modes, distinct from the catcher's per-org garment reasons). */
export function useFilmRedoReasons(): RedoReasonOption[] {
  const { t } = useTranslation('production');
  return useMemo<RedoReasonOption[]>(
    () => [
      { value: 'misprint', label: t('filmPrint.redoReasons.misprint'), description: t('filmPrint.redoReasons.misprintDesc') },
      { value: 'head_strike', label: t('filmPrint.redoReasons.headStrike'), description: t('filmPrint.redoReasons.headStrikeDesc') },
      { value: 'adhesion', label: t('filmPrint.redoReasons.adhesion'), description: t('filmPrint.redoReasons.adhesionDesc') },
      { value: 'damaged', label: t('filmPrint.redoReasons.damaged'), description: t('filmPrint.redoReasons.damagedDesc') },
    ],
    [t],
  );
}

export interface FilmRedoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void | Promise<void>;
  isPending: boolean;
}

export function FilmRedoDialog({ open, onOpenChange, onConfirm, isPending }: FilmRedoDialogProps) {
  const { t } = useTranslation('production');
  const reasons = useFilmRedoReasons();
  return (
    <RedoReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      isPending={isPending}
      reasons={reasons}
      copy={{
        selectTitle: t('filmPrint.redoSelectTitle'),
        confirmTitle: t('filmPrint.redoConfirmTitle'),
        confirmButtonLabel: t('filmPrint.redoConfirmButton'),
        pendingLabel: t('filmPrint.redoPending'),
        bodyText: t('filmPrint.redoConfirmBody'),
        confirmAriaLabel: (reason) => t('filmPrint.redoConfirmAria', { reason }),
        goBackAriaLabel: t('filmPrint.redoGoBackAria'),
      }}
    />
  );
}
